import React, { useContext, useRef, useState } from 'react'
import { AuthContext } from '../../../context/AuthContext'
import userAvatar from '../../../assets/default_avatar.png'
import { createPortal } from 'react-dom'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import toast from 'react-hot-toast'
import { User, Settings, LogOut, Upload, Camera, X } from 'lucide-react'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import useOutsideClick from '../../../hooks/useOutsideClick'

export function UserDropdown() {
  const { googleSignOut, microsoftSignOut, user, setUser } = useContext(AuthContext)
  const [openMenu, setOpenMenu] = useState(false)
  const [portalStyle, setPortalStyle] = useState({})
  const containerRef = useRef(null)
  const menuRef = useRef(null)
  const [showProfileDialog, setShowProfileDialog] = useState(false)
  
  useOutsideClick([containerRef, menuRef], () => {
    if (openMenu) {
      setOpenMenu(false)
    }
  })
  
  async function handleSignOut() {
    try {
      const signInMethod = localStorage.getItem('signin_method')
      if (signInMethod === 'microsoft') {
        await microsoftSignOut()
      } else {
        await googleSignOut()
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleClick = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const menuWidth = 200; // Width of the menu
      
      // Calculate the left position to ensure the menu stays within the viewport
      let left = rect.left;
      if (left + menuWidth > windowWidth) {
        left = windowWidth - menuWidth - 10; // Add some padding from the right edge
      }
      
      setPortalStyle({
        position: 'absolute',
        top: `${rect.top + rect.height}px`,
        left: `${left}px`,
      });
    }
    setOpenMenu(!openMenu)
  }

  return (
    <>
      <div ref={containerRef} className='relative'>
        <button className='flex mx-2 items-center' onClick={handleClick}>
          {(user?.picture)
            ? <img className='h-8 w-8 mx-1 rounded-full border border-grey-200 dark:border-gray-700' src={user.picture} alt="user avatar" />
            : <img className='h-8 w-8 mx-1 rounded-full border border-grey-200 dark:border-gray-700' src={userAvatar} alt="user avatar" />
          }
          <svg className="w-4 h-4 mx-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 10">
            <path d="M15.434 1.235A2 2 0 0 0 13.586 0H2.414A2 2 0 0 0 1 3.414L6.586 9a2 2 0 0 0 2.828 0L15 3.414a2 2 0 0 0 .434-2.179Z" />
          </svg>
        </button>
        {openMenu && (
          createPortal(
            <div
              ref={menuRef}
              className="absolute py-2 w-[200px] z-20 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 shadow-lg"
              style={portalStyle}
            >
              <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">Signed in as</p>
                <p className="text-sm font-bold truncate dark:text-white">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email || ''}</p>
              </div>
              
              <ul className="mt-2">
                {/* Temporarily disabled profile option while we update the user settings flow */}
                {/* <li>
                  <button 
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-700 dark:text-gray-200"
                    onClick={() => {
                      setShowProfileDialog(true)
                      setOpenMenu(false)
                    }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    <span>Your Profile</span>
                  </button>
                </li> */}
                <li>
                  <ThemeToggle />
                </li>
                <li>
                  <button 
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-700 dark:text-gray-200"
                    onClick={handleSignOut}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    <span>Sign Out</span>
                  </button>
                </li>
              </ul>
            </div>,
            document.body
          )
        )}
      </div>

      {showProfileDialog && (
        <UserProfileDialog 
          user={user} 
          setUser={setUser}
          open={showProfileDialog} 
          onOpenChange={setShowProfileDialog} 
        />
      )}
    </>
  )
}

function UserProfileDialog({ user, setUser, open, onOpenChange }) {
  const [name, setName] = useState(user?.name || '')
  const [email, setEmail] = useState(user?.email || '')
  const [profileImage, setProfileImage] = useState(user?.picture || null)
  const [tempImageUrl, setTempImageUrl] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const customFetch = useCustomFetch()
  const fileInputRef = useRef(null)

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Create a preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setTempImageUrl(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSave = async () => {
    setIsLoading(true)
    try {
      // First handle image upload if new image exists
      let pictureUrl = user.picture
      if (tempImageUrl && tempImageUrl !== user.picture) {
        // This part would need to be implemented with proper upload API
        // For now, we'll just simulate it
        pictureUrl = tempImageUrl
      }

      // Update user profile
      const updatedUser = {
        ...user,
        name,
        email,
        picture: pictureUrl
      }

      // Call API to update user
      // Note: You would need to create this endpoint in the backend
      await customFetch('/auth/updateProfile', 'PUT', {
        name,
        email,
        picture: pictureUrl
      })

      // Update local state
      setUser(updatedUser)
      onOpenChange(false)
      toast.success('Profile updated successfully')
    } catch (error) {
      console.error(error)
      toast.error('Failed to update profile')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md dark:bg-gray-800">
        <DialogHeader>
          <DialogTitle className="dark:text-white">Edit Profile</DialogTitle>
          <DialogDescription className="dark:text-gray-300">
            Update your profile information
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Profile Picture */}
          <div className="flex flex-col items-center">
            <div className="relative h-24 w-24 mb-4">
              {(tempImageUrl || user?.picture) ? (
                <div className="relative w-full h-full">
                  <img
                    src={tempImageUrl || user.picture}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                  />
                  <button
                    onClick={() => {
                      setTempImageUrl(null)
                      if (fileInputRef.current) {
                        fileInputRef.current.value = ''
                      }
                    }}
                    className="absolute top-0 right-0 bg-white dark:bg-gray-700 p-1 rounded-full shadow-md"
                    style={{ display: tempImageUrl ? 'block' : 'none' }}
                  >
                    <X size={16} className="text-gray-600 dark:text-gray-300" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  <User size={36} className="text-gray-400 dark:text-gray-300" />
                </div>
              )}
              
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 bg-primary p-1.5 rounded-full shadow-md text-white hover:bg-primary/90"
              >
                <Camera size={16} />
              </button>
              <input 
                type="file" 
                className="hidden" 
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>
          </div>
          
          {/* Form Fields */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium dark:text-gray-200">
                Full Name
              </label>
              <Input 
                id="name" 
                value={name} 
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="dark:bg-gray-700 dark:text-white dark:border-gray-600"
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium dark:text-gray-200">
                Email
              </label>
              <Input 
                id="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                disabled={user.signin_method === 'google' || user.signin_method === 'microsoft'}
                className="dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:disabled:bg-gray-600 dark:disabled:text-gray-400"
              />
              {(user.signin_method === 'google' || user.signin_method === 'microsoft') && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Email cannot be changed for accounts linked with {user.signin_method === 'google' ? 'Google' : 'Microsoft'}.
                </p>
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading} 
                className="dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
